import { useTranslation } from "react-i18next";
import type { JSX } from "react";
import { MenuButton, MenuLink, menuItemHoverClassName } from "./Menu";
import { Button } from "./forms/Button";
import { Logo, Menu } from "./icons";
import { cn } from "packages/dsaui/src/utils";
import routes from "routes";

interface HeaderProps {
  loginFn: () => void;
  logoutFn: () => void;
  authenticated?: boolean;
}

export const Header = ({
  loginFn,
  logoutFn,
  authenticated = false,
}: HeaderProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <nav className="border-dsa-mid-gray sticky top-0 z-1 border-b bg-white">
      <div className="flex items-center justify-between px-4 py-6 md:px-8">
        <div className="grow">
          <a
            href={routes.internal.base}
            className="inline-flex items-center no-underline"
          >
            <span>
              <Logo />
            </span>
            <span className="text-dsa-black ml-6 hidden text-xl font-bold md:block">
              {t("header.title")}
            </span>
          </a>
        </div>
        <div className="relative">
          {authenticated ? (
            <>
              <button
                aria-label={t("header.menuButton")}
                className={cn(
                  menuItemHoverClassName,
                  "flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-900",
                )}
                popoverTarget={menuId}
                type="button"
              >
                <Menu />
                <span>{t("header.menuLabel")}</span>
              </button>
              <nav
                id={menuId}
                aria-label={t("header.menu")}
                className="border-dsa-mid-gray popover-open:block fixed inset-x-0 top-28 bottom-0 z-2 m-0 h-auto w-full max-w-none border bg-white p-0 sm:inset-auto sm:top-24 sm:right-8 sm:bottom-auto sm:w-64 sm:rounded-lg"
                popover="auto"
              >
                <div className="border-0">
                  <MenuLink
                    className={headerMenuItemClassName}
                    href={routes.internal.base}
                  >
                    {t("header.menuItems.home")}
                  </MenuLink>
                  <MenuLink
                    className={headerMenuItemClassName}
                    href={routes.internal.account}
                  >
                    {t("header.menuItems.account")}
                  </MenuLink>
                  <MenuLink
                    className={headerMenuItemClassName}
                    href={routes.internal.membership}
                  >
                    {t("header.menuItems.membership")}
                  </MenuLink>
                  <MenuButton
                    className={headerMenuItemClassName}
                    onClick={logoutFn}
                    popoverTarget={menuId}
                    popoverTargetAction="hide"
                  >
                    {t("signOut")}
                  </MenuButton>
                </div>
              </nav>
            </>
          ) : (
            <Button className="whitespace-nowrap" onClick={loginFn}>
              {t("signIn")}
            </Button>
          )}
        </div>
      </div>
    </nav>
  );
};

const menuId = "authenticated-menu";

const headerMenuItemClassName = cn(
  menuItemHoverClassName,
  "border-b border-gray-200 px-6 py-4 text-base leading-6 sm:px-4 sm:py-3",
);

export default Header;
